import { useState, FC, useMemo } from "react";
import { useParams } from "react-router";

import { UserCompany, UserPosition } from "@vviinn/user-managment-client-ts";

import { useAppSelector } from "../../store";
import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";

import { NamedRow } from "../../components/GeneralUI/NamedRow/NamedRow";
import { Restricted } from "../../components/Restricted";

import { UserRole, GlobalRolesTitle } from "../../user/types";

import { companyName } from "../../company";
import { getRoleTitleByIdentifier, roles } from "../../auth";
import { DetailedWrapper } from "../../ui/layout/general/DetailedWrapper/DetailedWrapper";
import { Section } from "../../ui/layout/detailedPages/Section";
import { RoleTable, TableData } from "../../ui/layout/molecules/RoleTable";
import { UserRoleBadge } from "../../user/role/UserRoleBadge";
import {
  DIVIDED_STYLES,
  GROUP_STYLES,
} from "../../ui/layout/detailedPages/styles";
import { SlideOverFieldNamedRow } from "../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverSingleField/SlideOverFieldNamedRow";
import { valueOrNA } from "../../helpers";
import { ChangePasswordAcceptableRow } from "../../user/ChangePasswordAcceptableRow";
import { handleUser } from "../../user/handleUser";
import {
  companyRoleToUserRole,
  findCompanyMemberRole,
} from "../../user/helpers";
import { AssignUserSlideOver } from "../../user/role/AssignUserSlideOver";
import { globalRolesMapping } from "../../user/role/helpers";
import { NA_MESSAGE } from "../../ui/constants";
import { isCompanyViewer } from "../../components/CompanyViewerGate";

const CHANGE_USER_ROLE_PERMISSION = "user:role:delete";

export const UserDetails: FC<React.PropsWithChildren> = () => {
  const currentUserId = useAppSelector((state) => state.user.id);
  const { userId } = useParams();

  return userId === currentUserId ? (
    <UserDetailsContent />
  ) : (
    <Restricted showError={true} requiredPagesPermission="users">
      <UserDetailsContent />
    </Restricted>
  );
};

const UserDetailsContent: FC<React.PropsWithChildren> = () => {
  const { userId } = useParams();
  const users = useAppSelector((state) => state.users.users);
  const currentUser = useAppSelector((state) => state.user);
  const isCurrentUsersPage = currentUser.id === userId;

  const [userAssignmentModalOpen, setUserAssignmentModalOpen] = useState(false);

  const handlerParams = useRequestHandlers();
  const {
    handleChangePassword,
    handleUnassignCompanyRole,
    handleUnassignGlobalRole,
    handleDeleteUser,
    handleSetPosition,
  } = handleUser(handlerParams);

  const user = users.find((u) => u.uid === userId);

  const companies = useAppSelector((state) => state.companies.companies);
  const currentCompanyId = useAppSelector((state) => state.company.company?.id);
  const currentCompanyOwners = useAppSelector(
    (state) => state.company.company?.owners,
  );
  const currentCompanyMembers = useAppSelector(
    (state) => state.company.company?.members,
  );
  const currentCompanyPermissions = useAppSelector(
    (state) => state.company.company?.featurePermissions,
  );

  const backLink = "/users";

  const isUserAssignedToCurrentCompany = useMemo(() => {
    return user?.assignedCompanies?.some(
      (company) => company.id === currentCompanyId,
    );
  }, [currentCompanyId, user?.assignedCompanies]);

  const unassignCompanyRole = async (member: UserCompany) => {
    if (!userId || !currentCompanyId) return;
    await handleUnassignCompanyRole(
      member.role,
      userId,
      currentCompanyId,
      member.id,
    );
  };

  const unassignGlobalRole = async (role: GlobalRolesTitle) => {
    if (!user) return;
    await handleUnassignGlobalRole(user.uid, role);
  };

  const deleteUser = async () => {
    if (!userId) return false;
    return await handleDeleteUser(
      userId,
      isUserAssignedToCurrentCompany ? currentCompanyId : undefined,
    );
  };

  const changePosition = async (position?: UserPosition["position"]) => {
    if (!user) return false;
    return await handleSetPosition(user.uid, position);
  };

  const showPage = useMemo(() => {
    return user || isCurrentUsersPage;
  }, [user, isCurrentUsersPage]);

  const userCredentials = useMemo(() => {
    if (!showPage) return;
    const referenceUser = isCurrentUsersPage ? currentUser : user;
    return {
      email: referenceUser?.email ?? user?.email,
      emailVerified: referenceUser?.emailVerified ?? user?.emailVerified,
      displayName: user?.displayName,
    };
  }, [user, currentUser, showPage, isCurrentUsersPage]);

  const tableData = useMemo(() => {
    const buildCompanyRoleRow = (userCompany: UserCompany): TableData => ({
      cells: [
        companyName(userCompany.id, companies),
        <UserRoleBadge type={companyRoleToUserRole(userCompany.role)} />,
      ],
      action: () => unassignCompanyRole(userCompany),
      permissions: CHANGE_USER_ROLE_PERMISSION,
    });

    const companyRolesData = user?.assignedCompanies?.map(buildCompanyRoleRow);
    const globalRoles = user?.roles.filter(
      (role) => role in globalRolesMapping,
    );

    // Only when the account has no visible role at all (company or global) — e.g. a
    // freshly invited Company Editor whose viewer permissions can't fetch the platform
    // user list — fall back to the always-visible currentCompany data (same source
    // CompanyMembers.tsx uses) to recover their one company role. A company viewer
    // can't see the member list either, so on their own page derive it from the
    // same permission proxy the viewer notice uses.
    const fallbackRole =
      !companyRolesData?.length && !globalRoles?.length && currentCompanyId
        ? (findCompanyMemberRole(
            {
              owners: currentCompanyOwners ?? [],
              members: currentCompanyMembers ?? [],
            },
            userId,
          ) ??
          (isCurrentUsersPage && isCompanyViewer(currentCompanyPermissions)
            ? "viewer"
            : undefined))
        : undefined;
    if (fallbackRole && currentCompanyId) {
      return [
        buildCompanyRoleRow({ id: currentCompanyId, role: fallbackRole }),
      ];
    }

    if (!globalRoles?.length) return companyRolesData;
    const globalRoleData: TableData[] = globalRoles.map((role) => ({
      action: () => unassignGlobalRole(role as GlobalRolesTitle),
      permissions: CHANGE_USER_ROLE_PERMISSION,
      cells: ["Global", <UserRoleBadge type={role as UserRole} />],
    }));
    if (!companyRolesData) return globalRoleData;
    return [...companyRolesData, ...globalRoleData];
  }, [
    user?.assignedCompanies,
    user?.roles,
    companies,
    currentCompanyId,
    currentCompanyOwners,
    currentCompanyMembers,
    currentCompanyPermissions,
    isCurrentUsersPage,
    userId,
  ]);

  return (
    <DetailedWrapper
      deleteAction={deleteUser}
      detailedPageType="detail"
      detailedPage="user"
      backLink={backLink}
    >
      <Section title="General information">
        {showPage && (
          <div className={GROUP_STYLES}>
            <div className={DIVIDED_STYLES}>
              {userCredentials?.displayName && (
                <NamedRow
                  title="Username"
                  content={userCredentials.displayName}
                />
              )}
              <NamedRow
                title="Email"
                content={userCredentials?.email ?? "unknown"}
              />
              <NamedRow
                title="Status"
                content={
                  userCredentials?.emailVerified
                    ? "Verified"
                    : userCredentials?.emailVerified === false
                      ? "Not verified"
                      : NA_MESSAGE
                }
              />
              {user && (
                <ChangePasswordAcceptableRow
                  userId={user.uid}
                  handleChangePassword={handleChangePassword}
                />
              )}

              <SlideOverFieldNamedRow
                onAccept={changePosition}
                namedRowTitle="Role"
                permissionsGroup="user:user"
                slideOverValue={valueOrNA(
                  getRoleTitleByIdentifier(user?.position),
                )}
                options={roles.map(({ identifier, title }) => ({
                  title,
                  value: identifier,
                }))}
              />
            </div>
          </div>
        )}
      </Section>

      <Section
        title="Assigned roles"
        buttonProperties={{
          createPermissions: "user:role:create",
          buttonTitle: "Add new role",
          iconType: "newUser",
          onClick: () => setUserAssignmentModalOpen(true),
        }}
      >
        <RoleTable tbData={tableData} fallbackMessage="No roles assigned" />
      </Section>

      {user && (
        <AssignUserSlideOver
          open={userAssignmentModalOpen}
          companies={companies}
          user={user}
          onClose={() => setUserAssignmentModalOpen(false)}
        />
      )}
    </DetailedWrapper>
  );
};
